import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
    CUSTOM_ELEMENTS_SCHEMA,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    Injector,
    Output,
    Signal,
    ViewEncapsulation,
    computed,
    input,
    signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTreeModule } from '@angular/material/tree';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
    FolderType,
    ProcessFlatTreeNode,
    ProcessTreeComponent,
    ProcessTreeNode,
    TypeOfProcess,
    TypeOfProcessTreeNode,
} from '@em4cloud/process-tree';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Area, Process } from 'app/api';
import { AlertService } from 'app/core/alert/alert.service';
import { RoleService } from 'app/core/role-management/role-management.service';
import { SaveButtonService } from 'app/core/save-button/save-button.service';
import { AreaService } from 'app/modules/org-sys-area/services/area.service';
import { TestScenariosService } from 'app/modules/test/manage-scenarios/test-scenarios/test-scenarios.service';
import { TestService } from 'app/modules/test/test.service';
import { OrgNavigationService } from 'app/services/org-nav.service';
import { LanguageChangerService } from 'app/services/translate_api/languageChanger.service';
import { Observable, of } from 'rxjs';
import { ProcessMenuService } from '../../process-menu.service';
import { ProcessService } from '../../process.service';
import { ProcessesFilterComponent } from '../processes-filter/processes-filter.component';
import { ProcessesTreeComponent } from './processes-tree.component';
import { ProcessTreeService } from './processes-tree.service';

@Component({
    selector: 'master-process-selection-tree',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        MatButtonModule,
        MatMenuModule,
        MatTreeModule,
        MatIconModule,
        DragDropModule,
        MatCheckboxModule,
        TranslocoModule,
        ProcessesFilterComponent,
        ProcessTreeComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    exportAs: 'processes-tree',
    templateUrl: './processes-tree.component.html',
    styleUrls: ['./processes-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MasterProcessSelectionTreeComponent extends ProcessesTreeComponent {
    @Output() onTreeNodeSelectionChanged: EventEmitter<string> = new EventEmitter();

    processes = input<Process[]>([]);

    override processData = computed<Process[]>(() => this.processes());

    override transformFunction = signal(
        (node: ProcessTreeNode, level: number): ProcessFlatTreeNode => {
            let flatNode: ProcessFlatTreeNode = {
                id: node.id,
                type: node.type,
                folderType: node.folderType,
                expandable: !!node.children,
                level: level,
                title: node.title,
                status: node.meta?.status,
                area: node.meta?.area,
                childrenCount: node.meta?.childrenCount,
                versionCount: node.meta?.versionCount,
            };
            if (
                this.type === TypeOfProcess.Scenario &&
                node.type === TypeOfProcessTreeNode.Scenario
            ) {
                flatNode.title = `${node.title} Scenario`;
                flatNode.checkbox = true;
                flatNode.icon = 'assignment';
            } else if (node.type === TypeOfProcessTreeNode.Folder) {
                flatNode.title = node.title;
                flatNode.icon = node.folderType === FolderType.Area ? 'folder' : 'file_copy';
            } else {
                flatNode.title = node.title;
                flatNode.checkbox =
                    this.type === TypeOfProcess.Scenario
                        ? false
                        : !node.meta.linkedToSubprocessId && node.id !== this.selectedProcess()?.id;
            }
            return flatNode;
        }
    );

    // TODO: Should be removed
    override mdoAreas: Signal<Area[]> = signal([
        {
            id: '7a75da44-d3f6-49b8-901e-12d9fcbb4083',
            versionId: '184449e2-917e-4158-9a7e-a0a525d28d56',
            title: 'Finance Master',
            code: 'FIN-M',
            internalId: null,
            organisation: '00000000-0000-0000-0000-000000000000',
            children: [],
            infoTypes: null,
            metadata: null,
            type: 'area',
            status: 'ACTIVE',
            version: null,
            updated: '2024-12-16T08:50:57.046Z',
        },
    ]);

    constructor(
        _processService: ProcessService,
        _processTreeService: ProcessTreeService,
        inject: Injector,
        _translocoService: TranslocoService,
        _processMenuService: ProcessMenuService,
        _testScenarioService: TestScenariosService,
        _testService: TestService,
        route: ActivatedRoute,
        orgNav: OrgNavigationService,
        _destroyRef: DestroyRef,
        languageChangerService: LanguageChangerService,
        _areaService: AreaService,
        roleService: RoleService,
        alertService: AlertService,
        changeDetector: ChangeDetectorRef,
        _saveButtonService?: SaveButtonService
    ) {
        super(
            _processService,
            _processTreeService,
            inject,
            _translocoService,
            _processMenuService,
            _testScenarioService,
            _testService,
            route,
            orgNav,
            _destroyRef,
            languageChangerService,
            _areaService,
            roleService,
            alertService,
            changeDetector,
            _saveButtonService
        );
    }

    override async handleTreeNodeSelection(params: ParamMap): Promise<void> {}

    override treeNodeSelectionChanged(node: ProcessFlatTreeNode): void {
        this.onTreeNodeSelectionChanged.emit(node.id);
    }
    override canChangeTreeNodeSelection(node: ProcessFlatTreeNode): Observable<boolean> | boolean {
        return of(true);
    }
}
